import Select, { SelectChangeEvent } from '@mui/material/Select';

import FormControl from '@mui/material/FormControl';
import { ISearchFilter } from '@orcha/types/search';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import React from 'react';
import { logEventAnalytic } from 'src/utilities/logAnalytics';
import { styled } from '@mui/material/styles';
import theme from 'src/themes';
import useTranslation from 'next-translate/useTranslation';
import FilterMenuItem from '../../FilterMenuItem';

const StyledFormControl = styled(FormControl)(() => ({
  backgroundColor: theme.palette.common.white,
}));

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 400,
    },
  },
};

type ArrayFilterProps = {
  filter: ISearchFilter;
  selectedOptions: string[];
  handleSelectedChange: (selectedOptions: string[]) => void;
};

export const ArrayFilter = (props: ArrayFilterProps) => {
  const { filter, selectedOptions, handleSelectedChange } = props;

  const { t } = useTranslation('search');

  if (filter.dataType !== 'StringArray') {
    return <></>;
  }

  const handleChange = (event: SelectChangeEvent<typeof selectedOptions>) => {
    const {
      target: { value },
    } = event;
    const label = t(`filterLabel.${filter.filterName.toLowerCase()}`);
    logEventAnalytic({
      category: 'Filters',
      action: 'Filter clicked on search page - microsite',
      label: `${value} - ${label}`,
    });
    handleSelectedChange(typeof value === 'string' ? value.split(',') : value);
  };

  const getRenderValue = (selected: string[]) => {
    if (filter.isValueTranslationRequired) {
      return selected
        .map((value) =>
          t(
            `filterOption.${filter.filterName.toLocaleLowerCase()}.${value.toLocaleLowerCase()}`
          )
        )
        .join(', ');
    }
    return selected.join(', ');
  };

  return (
    <StyledFormControl fullWidth size="small">
      <InputLabel id={`filter-label-${filter.filterName}`}>
        {t(`filterLabel.${filter.filterName.toLowerCase()}`)}
      </InputLabel>
      <Select
        labelId={`filter-label-${filter.filterName}`}
        multiple
        value={selectedOptions}
        onChange={handleChange}
        label={filter.filterName}
        renderValue={getRenderValue}
        variant="outlined"
        MenuProps={MenuProps}
        data-testid={`filter-select-${filter.filterName.replace(
          /[A-Z]/g,
          (m) => `-${m.toLowerCase()}`
        )}`}
      >
        {filter.filterValues
          ?.sort((a, b) => (a > b ? 1 : -1))
          .map((option) => {
            return (
              <MenuItem
                key={option}
                value={option}
                data-testid={`filter-menu-item-${filter.filterName}`}
              >
                <FilterMenuItem
                  key={option}
                  filterName={filter.filterName}
                  option={option}
                  isChecked={selectedOptions.indexOf(option) > -1}
                  requiresTranslation={filter.isValueTranslationRequired}
                />
              </MenuItem>
            );
          })}
      </Select>
    </StyledFormControl>
  );
};
